import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { SITE } from "../constants/site";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const navItems = [
    { label: "Features", href: "/#features" },
    { label: "Privacy", href: "/privacy" },
    { label: "Terms", href: "/terms" },
    { label: "Account Deletion", href: "/account-deletion" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/75 border-b border-slate-200/70"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-3 group"
                    onClick={() => setOpen(false)}
                >
                    <img
                        src={SITE.logo}
                        alt={`${SITE.appName} logo`}
                        className="h-9 w-9 rounded-xl shadow-sm ring-1 ring-slate-200"
                    />
                    <span className="font-display font-semibold text-lg tracking-tight text-slate-900">
                        {SITE.appName}
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    {navItems.map((item) => (
                        <Link
                            key={item.label}
                            to={item.href}
                            data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:brand-gradient after:transition-all hover:after:w-full"
                        >
                            {item.label}
                        </Link>
                    ))}
                </nav>

                <div className="hidden md:block">
                    <Button
                        asChild
                        data-testid="nav-cta-button"
                        className="rounded-full brand-gradient text-white hover:opacity-95 hover:-translate-y-0.5 transition-all shadow-md shadow-brand-purple/20 border-0"
                    >
                        <a href={location.pathname === "/" ? "#waitlist" : "/#waitlist"}>Get Early Access</a>
                    </Button>
                </div>

                <button
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                    className="md:hidden p-2 rounded-full hover:bg-slate-100"
                    onClick={() => setOpen((v) => !v)}
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="md:hidden bg-white border-b border-slate-200 px-5 py-4 space-y-3"
                >
                    {navItems.map((item) => (
                        <Link
                            key={item.label}
                            to={item.href}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="block text-slate-700 font-medium py-2"
                        >
                            {item.label}
                        </Link>
                    ))}
                    <Button
                        asChild
                        data-testid="nav-mobile-cta"
                        className="w-full rounded-full brand-gradient text-white border-0"
                    >
                        <a href="/#waitlist" onClick={() => setOpen(false)}>
                            Get Early Access
                        </a>
                    </Button>
                </div>
            )}
        </header>
    );
};
